import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { getBuildStatusIconComponent } from '@/features/shared/components/tableSystem/buildStatusIcons';
import { TableBuildStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableBuildStatusBreakdownCell';
import {
  CLONE_META_DETAIL_GLYPH_SX,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { TableMetricsBreakdownCell } from '@/features/shared/components/tableSystem/TableMetricsBreakdownCell';
import { resolveEntityTags, resolveTagColor } from '@/features/shared/tags';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from '@/features/suites/components/suitesColumns.latestCellStyles';
import { TableActionsCell, TableCellContainer } from '@/features/tests/components/testsTableCells';
import { mapChipKeyToStatusTone } from '@/features/tests/components/testsTableShared';
import {
  Box,
  formatDuration,
  formatFrequency,
  formatPercent,
  formatStatusLabel,
  resolveStatusChipTone,
} from '@/ui';
import { getStringFilterOperators } from '@/ui/composites/grid/gridOperators';
import { TableNameCell, getThresholdChipKey } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import type { TagColor } from '@/shared/data/tags';
import type { SummaryTooltipItem } from '@/ui/composites/table/tableUtils';
import type { GridColDef, GridRenderCellParams } from '@/ui/primitives';
import type { BuildStatus, MetricThresholdGroup, PipelineListItemDto } from '@flowqa/shared';

type PipelinesColumnsOptions = {
  selectedProjectId: string | null;
  thresholds: MetricThresholdGroup;
  resolveThresholds: (row: { projectId: string }) => MetricThresholdGroup | undefined;
  tagColorByName?: Partial<Record<string, TagColor>>;
  onStatusSelect?: (status: BuildStatus | 'none') => void;
  showDeltas?: boolean;
  includeActionsColumn?: boolean;
  onDeleteRow?: (row: PipelineListItemDto) => void;
  deleteRowPendingId?: string | null;
};

const columnFlex = {
  name: 0.9,
  latest: 0.55,
  builds: 0.55,
  metrics: 0.9,
  actions: 0.1,
};

const mapPipelineBuildSummaryItems = (row: PipelineListItemDto): SummaryTooltipItem[] =>
  (row.buildSummaries ?? []).map((build) => {
    const runNumber = resolveBuildRunNumber(build.buildKey ?? null);
    const pipelineSlug = resolvePipelineSlug(row);

    return {
      key: build.id,
      detailLabel: row.name,
      executionLabel: runNumber === null ? null : `#${runNumber}`,
      href: `/pipelines/${pipelineSlug}`,
      executionHref: runNumber === null ? null : `/pipelines/${pipelineSlug}/runs/${runNumber}`,
      runNumber,
      status: build.status,
    };
  });

export const buildPipelinesColumns = ({
  selectedProjectId,
  thresholds,
  resolveThresholds,
  tagColorByName = {},
  onStatusSelect,
  showDeltas = true,
  includeActionsColumn = false,
  onDeleteRow,
  deleteRowPendingId = null,
}: PipelinesColumnsOptions): Array<GridColDef<PipelineListItemDto>> => {
  const stringOperators = getStringFilterOperators();
  const deltasEnabled = showDeltas;
  const resolvedThresholds = (row: PipelineListItemDto): MetricThresholdGroup => {
    if (selectedProjectId) return thresholds;
    return resolveThresholds(row) ?? thresholds;
  };

  const nameColumn: GridColDef<PipelineListItemDto> = {
    field: 'name',
    headerName: 'Pipeline',
    flex: columnFlex.name,
    align: 'left',
    headerAlign: 'left',
    filterOperators: stringOperators,
    renderCell: (params: GridRenderCellParams<PipelineListItemDto>) => (
      <TableCellContainer gap="none">
        <TableNameCell
          primary={{
            label: params.row.name,
            href: `/pipelines/${resolvePipelineSlug(params.row)}`,
          }}
          tags={resolveEntityTags(params.row).map((tag) => ({
            label: tag,
            tone: resolveTagColor(tag, tagColorByName),
          }))}
        />
      </TableCellContainer>
    ),
  };

  const latestStatusColumn: GridColDef<PipelineListItemDto> = {
    field: 'latestBuildStatus',
    headerName: 'Latest Status',
    flex: columnFlex.latest,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: true,
    renderCell: (params: GridRenderCellParams<PipelineListItemDto>) => {
      const latestBuild = params.row.latestBuild ?? null;
      const status = latestBuild?.status ?? null;
      const statusLabel = status ? formatStatusLabel(status) : 'No Builds';
      const statusTone = resolveStatusChipTone(status);
      const StatusIcon = status ? getBuildStatusIconComponent(status) : null;
      const durationLabel =
        latestBuild?.durationMs !== null && latestBuild?.durationMs !== undefined
          ? formatDuration(latestBuild.durationMs)
          : '';
      const timestamp = latestBuild?.timestamp ?? null;
      const timestampLabel = timestamp ? formatRelativeTime(timestamp) : '';
      const timestampTitle = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
      const runNumber = resolveBuildRunNumber(latestBuild?.buildKey ?? null);
      const runHref =
        runNumber !== null && status
          ? `/pipelines/${resolvePipelineSlug(params.row)}/runs/${runNumber}`
          : null;

      return (
        <TableCellContainer gap="none">
          <TableStatusMetaContent
            statusLabel={statusLabel}
            statusHref={runHref}
            onStatusClick={(event) => {
              event.stopPropagation();
              if (!status || runHref) return;
              onStatusSelect?.(status);
            }}
            chipTone={statusTone}
            chipIcon={StatusIcon ? <Box component={StatusIcon} /> : <Box />}
            chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
            chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
            durationLabel={durationLabel}
            durationIcon={<TimerOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
            timestampLabel={timestampLabel}
            timestampTitle={timestampTitle}
            timestampIcon={<EventOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
          />
        </TableCellContainer>
      );
    },
  };

  const buildsColumn: GridColDef<PipelineListItemDto> = {
    field: 'buildStatusCounts',
    headerName: 'Builds',
    flex: columnFlex.builds,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    renderCell: (params: GridRenderCellParams<PipelineListItemDto>) => {
      const total = params.row.buildTotal ?? 0;

      return (
        <TableCellContainer gap="none">
          <TableBuildStatusBreakdownCell
            total={total}
            counts={params.row.buildStatusCounts}
            summaryItems={mapPipelineBuildSummaryItems(params.row)}
          />
        </TableCellContainer>
      );
    },
  };

  const metricsColumn: GridColDef<PipelineListItemDto> = {
    field: 'metricsSummary',
    headerName: 'Metrics',
    flex: columnFlex.metrics,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    renderCell: (params: GridRenderCellParams<PipelineListItemDto>) => {
      const passRate = params.row.metrics?.passRate ?? null;
      const passRateDelta = deltasEnabled ? (params.row.metrics?.delta?.passRate ?? null) : null;
      const frequency = params.row.metrics?.frequencyPerDay ?? null;
      const frequencyDelta = deltasEnabled
        ? (params.row.metrics?.delta?.frequencyPerDay ?? null)
        : null;
      const avgDuration = params.row.metrics?.averageDurationMs ?? null;
      const avgDurationDelta = deltasEnabled
        ? (params.row.metrics?.delta?.averageDurationMs ?? null)
        : null;
      const thresholdGroup = resolvedThresholds(params.row);
      const passRateTone = mapChipKeyToStatusTone(
        getThresholdChipKey(passRate, thresholdGroup.passRate),
      );
      const frequencyTone = mapChipKeyToStatusTone(
        getThresholdChipKey(frequency, thresholdGroup.frequency),
      );
      const avgDurationTone = mapChipKeyToStatusTone(
        getThresholdChipKey(avgDuration, thresholdGroup.avgDuration),
      );

      return (
        <TableCellContainer gap="none">
          <TableMetricsBreakdownCell
            items={[
              {
                key: 'passRate',
                label: 'Pass Rate',
                valueLabel: passRate === null ? 'No data' : `${formatPercent(passRate)}%`,
                deltaValue: passRate === null ? null : passRateDelta,
                deltaLabel:
                  passRate === null || passRateDelta === null
                    ? null
                    : `${passRateDelta > 0 ? '+' : ''}${formatPercent(passRateDelta)}%`,
                isMissing: passRate === null,
                tone: passRateTone,
                direction: thresholdGroup.passRate.direction,
              },
              {
                key: 'frequency',
                label: 'Frequency',
                valueLabel: frequency === null ? 'No data' : formatFrequency(frequency),
                deltaValue: frequency === null ? null : frequencyDelta,
                deltaLabel:
                  frequency === null || frequencyDelta === null
                    ? null
                    : `${frequencyDelta > 0 ? '+' : frequencyDelta < 0 ? '-' : ''}${formatFrequency(
                        Math.abs(frequencyDelta),
                      )}`,
                isMissing: frequency === null,
                tone: frequencyTone,
                direction: thresholdGroup.frequency.direction,
              },
              {
                key: 'avgDuration',
                label: 'Avg. Duration',
                valueLabel: avgDuration === null ? 'No data' : formatDuration(avgDuration),
                deltaValue: avgDuration === null ? null : avgDurationDelta,
                deltaLabel:
                  avgDuration === null || avgDurationDelta === null
                    ? null
                    : `${avgDurationDelta > 0 ? '+' : '-'}${formatDuration(
                        Math.abs(avgDurationDelta),
                        { roundMillisecondsWithSeconds: true },
                      )}`,
                isMissing: avgDuration === null,
                tone: avgDurationTone,
                direction: thresholdGroup.avgDuration.direction,
              },
            ]}
          />
        </TableCellContainer>
      );
    },
  };

  const actionsColumn: GridColDef<PipelineListItemDto> = {
    field: 'actions',
    headerName: '',
    flex: columnFlex.actions,
    minWidth: 56,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<PipelineListItemDto>) => (
      <TableActionsCell
        rowName={params.row.name}
        showEditTagsAction={false}
        onDelete={() => {
          onDeleteRow?.(params.row);
        }}
        disableDelete={deleteRowPendingId === params.row.id}
      />
    ),
  };

  return [
    nameColumn,
    latestStatusColumn,
    buildsColumn,
    metricsColumn,
    ...(includeActionsColumn ? [actionsColumn] : []),
  ];
};
